import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Download, Loader2, PartyPopper, AlertTriangle, ShieldCheck, X } from 'lucide-react';
import '../styles/pricing.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = 'https://customer-assets-lqy194kg.emergentagent.net/job_31bcde7c-d98a-4c27-a3ab-d96f08806001/artifacts/e3mvklhv_Logo%20Pink.png';
const gbp = (n) => `£${Math.round(n || 0).toLocaleString('en-GB')}`;

export default function AcceptQuote() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [quote, setQuote] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Accept your quote | Boost!';
    axios.get(`${API}/quote/${id}?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setQuote(r.data);
        if (r.data.status === 'accepted') setAccepted(true);
        if (r.data.status === 'declined') setDeclined(true);
      })
      .catch((err) => setLoadError(err.response?.status === 403 ? 'This link is invalid or has expired. Please contact us for a fresh quote.' : 'We couldn\'t find that quote. Please check your link or contact us.'));
  }, [id, token]);

  const accept = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/quote/${id}/accept`, { token });
      setAccepted(true);
    } catch (err) {
      setLoadError(err.response?.status === 410 ? 'This quote has expired. Please contact us for an up-to-date quote.' : 'Something went wrong. Please try again or contact us.');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!window.confirm('Decline this quote? You can always request a new one later.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/quote/${id}/decline`, { token });
      setDeclined(true);
    } catch (err) {
      setLoadError(err.response?.status === 409 ? 'This quote has already been accepted.' : 'Something went wrong. Please try again or contact us.');
    } finally {
      setBusy(false);
    }
  };

  const Header = (
    <header className="pp-header">
      <div className="pp-header-inner">
        <Link to="/" aria-label="BOOST! home"><img src={LOGO} alt="Boost! Design & Marketing" /></Link>
        <div className="pp-header-links">
          <Link to="/" className="pp-header-link">Main site</Link>
          <Link to="/website-design-pricing" className="pp-header-link">Pricing</Link>
        </div>
      </div>
    </header>
  );

  return (
    <div className="pricing-page" data-testid="accept-quote-page">
      {Header}
      <section className="pp-section" style={{ paddingTop: 40 }}>
        <div className="pp-container">
          {loadError && (
            <div className="qb-success" data-testid="accept-error">
              <span className="qb-success-icon" style={{ background: '#FBE0E0', color: '#C0392B' }}><AlertTriangle size={28} /></span>
              <h2 className="pp-display">Hmm, that didn't work</h2>
              <p>{loadError}</p>
              <div className="qb-success-btns">
                <Link className="pp-btn pp-btn-primary" to="/quote">Build a new quote</Link>
              </div>
            </div>
          )}

          {!loadError && !quote && (
            <p className="pp-lead" style={{ textAlign: 'center' }}><Loader2 size={16} className="qb-spin" /> Loading your quote…</p>
          )}

          {!loadError && quote && accepted && (
            <div className="qb-success" data-testid="accept-confirmed">
              <span className="qb-success-icon"><PartyPopper size={30} /></span>
              <h2 className="pp-display">Quote {quote.reference} accepted 🎉</h2>
              <p>Brilliant — thank you! We've let the Boost! team know and we'll be in touch shortly to get your project moving.</p>
              <div className="qb-success-btns">
                <a className="pp-btn pp-btn-primary" href={`${API}/quote/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="accept-download-pdf">
                  <Download size={16} /> Download your quote
                </a>
                <Link className="pp-btn pp-btn-ghost" to="/">Back to site</Link>
              </div>
            </div>
          )}

          {!loadError && quote && declined && !accepted && (
            <div className="qb-success" data-testid="accept-declined">
              <span className="qb-success-icon" style={{ background: '#EDEDF4', color: '#55557A' }}><X size={28} /></span>
              <h2 className="pp-display">Quote {quote.reference} declined</h2>
              <p>No problem at all — thanks for letting us know. If anything changes or you'd like a fresh quote, we're only an email away.</p>
              <div className="qb-success-btns">
                <Link className="pp-btn pp-btn-primary" to="/quote">Build a new quote</Link>
                <Link className="pp-btn pp-btn-ghost" to="/">Back to site</Link>
              </div>
            </div>
          )}

          {!loadError && quote && !accepted && !declined && (
            <div className="qb-grid" style={{ gridTemplateColumns: '1fr 400px' }}>
              <div className="qb-select">
                <span className="pp-kicker">Review &amp; accept</span>
                <h1 className="pp-display" data-testid="accept-h1" style={{ fontSize: 34, margin: '4px 0 6px' }}>QUOTE {quote.reference}</h1>
                <p className="pp-lead">Hi {quote.name}, here's your quote from Boost! Review the details and accept online when you're ready.</p>
                {quote.expired && (
                  <p className="ed-action-error" data-testid="accept-expired-note">Heads up: this quote has passed its 30-day validity — accepting may be subject to a re-quote.</p>
                )}
                <div className="qb-group" style={{ marginTop: 18 }}>
                  <span className="qb-group-label">What's included</span>
                  <div className="qb-items">
                    {(quote.lines || []).map((l, i) => (
                      <div className="qb-item" key={i} style={{ cursor: 'default' }} data-testid={`accept-line-${i}`}>
                        <span className="qb-item-check" style={{ background: '#E60065', borderColor: '#E60065' }}><Check size={14} /></span>
                        <span className="qb-item-body">
                          <span className="qb-item-name">{l.label}</span>
                          <span className="qb-item-desc">{l.category}{l.unit === 'monthly' ? ' · monthly' : ' · one-off'}</span>
                        </span>
                        <span className="qb-item-price">{gbp(l.line_total_ex)}<small>{l.unit === 'monthly' ? ' + VAT / mo' : ' + VAT'}</small></span>
                      </div>
                    ))}
                  </div>
                </div>
                {quote.notes && <p className="pp-lead" style={{ fontSize: 14 }}><strong>Notes:</strong> {quote.notes}</p>}
              </div>

              <div className="qb-summary">
                <span className="qb-summary-kicker"><ShieldCheck size={15} /> Your totals</span>
                <div className="qb-totals" style={{ borderTop: 'none', paddingTop: 8, marginTop: 12 }}>
                  {quote.subtotal_oneoff_ex > 0 && (
                    <div><span>One-off (inc VAT)</span><strong data-testid="accept-total-oneoff">{gbp(quote.total_oneoff_inc)}</strong></div>
                  )}
                  {quote.subtotal_monthly_ex > 0 && (
                    <div><span>Monthly (inc VAT)</span><strong data-testid="accept-total-monthly">{gbp(quote.total_monthly_inc)} /mo</strong></div>
                  )}
                </div>
                <button className="pp-btn pp-btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={accept} disabled={busy} data-testid="accept-button">
                  {busy ? <><Loader2 size={16} className="qb-spin" /> Accepting…</> : <>Accept this quote <Check size={16} /></>}
                </button>
                <a className="pp-btn pp-btn-ghost" style={{ width: '100%', marginTop: 10 }} href={`${API}/quote/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="accept-view-pdf">
                  <Download size={15} /> View PDF
                </a>
                <button className="qb-decline-link" onClick={decline} disabled={busy} data-testid="decline-button">
                  No thanks — decline this quote
                </button>
                <p className="qb-note">By accepting you're confirming you're happy to proceed. We'll follow up to finalise a written proposal and next steps.</p>
              </div>
            </div>
          )}
        </div>
      </section>
      <footer className="qb-page-footer" data-testid="accept-footer">
        <img src={LOGO} alt="Boost! Design & Marketing" />
        <p>© {new Date().getFullYear()} Boost! Design &amp; Marketing Ltd · <Link to="/">boostmarketing.agency</Link></p>
      </footer>
    </div>
  );
}
